// Per-layer thumbnail engine. Renders the composite up to and including each
// layer at a small fixed size, cached against state hash. Used by the layer
// list to show what each layer contributes.

import { createPipeline } from '@/core/pipeline';
import { fnv1a, stableStringify } from '@/lib/hash';
import type { AppState } from '@/core/types';

export const THUMB_SIZE = 40;

let pipeline: ReturnType<typeof createPipeline> | null = null;
const renderCache = new Map<string, string>(); // layerId -> dataURL

function getPipeline(): ReturnType<typeof createPipeline> {
  if (pipeline === null) pipeline = createPipeline();
  return pipeline;
}

function hashLayerStack(layers: AppState['layers']): string {
  return fnv1a(stableStringify(layers.map((l) => ({ k: l.kind, p: l.params, s: l.seed, b: l.blend, o: l.opacity, e: l.enabled }))));
}

export type ThumbnailMap = Map<string, string>; // layerId -> data URL

let inflight = false;
let pending: AppState | null = null;
let listeners: ((m: ThumbnailMap) => void)[] = [];

export function subscribeThumbnails(fn: (m: ThumbnailMap) => void): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

export function requestThumbnails(state: AppState): void {
  pending = state;
  if (inflight) return;
  void runLoop();
}

async function runLoop(): Promise<void> {
  inflight = true;
  while (pending !== null) {
    const snapshot = pending;
    pending = null;
    await renderAll(snapshot);
  }
  inflight = false;
}

async function renderAll(state: AppState): Promise<void> {
  const layers = state.layers;
  if (layers.length === 0) {
    listeners.forEach((l) => l(new Map()));
    return;
  }
  const stackHash = hashLayerStack(layers);
  const out = new Map<string, string>();
  const p = getPipeline();
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i]!;
    const cacheKey = `${layer.id}|${stackHash}|${i}`;
    const cached = renderCache.get(cacheKey);
    if (cached !== undefined) {
      out.set(layer.id, cached);
      continue;
    }
    const subState: AppState = {
      ...state,
      resolution: { w: THUMB_SIZE, h: THUMB_SIZE },
      layers: layers.slice(0, i + 1),
      soloLayerId: null,
      time: 0,
      paused: true,
    };
    const result = await p.render(subState);
    if (!result.image) continue;
    const url = imageDataToUrl(result.image);
    if (url) {
      renderCache.set(cacheKey, url);
      out.set(layer.id, url);
    }
  }
  listeners.forEach((l) => l(out));

  // Trim cache to bound memory.
  if (renderCache.size > 256) {
    const overflow = renderCache.size - 256;
    const keys = renderCache.keys();
    for (let i = 0; i < overflow; i++) {
      const k = keys.next().value;
      if (k !== undefined) renderCache.delete(k);
    }
  }
}

function imageDataToUrl(image: ImageData): string | null {
  const c = document.createElement('canvas');
  c.width = image.width;
  c.height = image.height;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.putImageData(image, 0, 0);
  return c.toDataURL('image/png');
}
